import StyledMiniBasketPaymentSchedule from '@components/Organisms/MiniBasket/styled/StyledMiniBasketPaymentSchedule';
import { MonthlyPaymentT } from '@lib/types';
import { getFormattedPrice } from '@lib/utils';
import React, { useMemo } from 'react';
import {
  getMonthsUntilNextApril,
  calculateTotalCostForMonth,
} from '@components/Organisms/MiniBasket/paymentScheduleFunctions';

const MiniBasketPaymentSchedule = ({
  monthlyPayment,
  monthsProduct,
  fullWidth = false,
  annualIncrease,
}: {
  monthlyPayment: MonthlyPaymentT;
  monthsProduct: number;
  fullWidth?: boolean;
  annualIncrease?: number;
}) => {
  const { details } = monthlyPayment;

  const schedule = useMemo(() => {
    const months = Math.max(1, monthsProduct);

    const monthsUntilNextApril = getMonthsUntilNextApril();
    const schedule: Array<{
      month: number;
      span: number;
      price: number;
      isIncreasedPrice: boolean;
    }> = [];

    let lastPrice = -1;
    let lastIsIncreasedPrice = false;
    let spanStart = 1;

    for (let monthNum = 1; monthNum <= months; monthNum++) {
      const price = calculateTotalCostForMonth(
        monthNum,
        details,
        monthsUntilNextApril,
        Number(annualIncrease) || 0,
      );

      const isIncreasedPrice =
        monthNum > monthsUntilNextApril || monthNum > monthsUntilNextApril + 12;

      if (price !== lastPrice || isIncreasedPrice !== lastIsIncreasedPrice) {
        if (monthNum > 1) {
          schedule.push({
            month: spanStart,
            span: monthNum - spanStart,
            price: lastPrice,
            isIncreasedPrice: lastIsIncreasedPrice,
          });
          spanStart = monthNum;
        }
      }

      lastPrice = price;
      lastIsIncreasedPrice = isIncreasedPrice;
    }

    schedule.push({
      month: spanStart,
      span: months - spanStart + 1,
      price: lastPrice,
      isIncreasedPrice: lastIsIncreasedPrice,
    });

    return schedule;
  }, [monthsProduct, details, annualIncrease]);

  return (
    <StyledMiniBasketPaymentSchedule fullWidth={fullWidth}>
      <strong>Payment schedule</strong>
      <ul>
        {schedule.map(({ month, span, price, isIncreasedPrice }) => (
          <li key={month}>
            <span>{`Month ${month}${span > 1 ? `-${month + span - 1}` : ''}`}</span>
            <span className="divider" />
            <span>
              {`£${getFormattedPrice(price)}`}
              {isIncreasedPrice ? <span className="price-note">*</span> : null}
            </span>
          </li>
        ))}
      </ul>
      {!!(annualIncrease && schedule.some(({ isIncreasedPrice }) => isIncreasedPrice)) && (
        <div className="price-increase-note">
          * This includes your annual April price increase of £{getFormattedPrice(annualIncrease)}
        </div>
      )}
    </StyledMiniBasketPaymentSchedule>
  );
};

export default MiniBasketPaymentSchedule;
